import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BulkFileRow } from "@/components/bulk/BulkFileRow";
import { BulkGate } from "@/components/bulk/BulkGate";
import { RefusedUploads } from "@/components/RefusedUploads";
import { UploadCloud, Loader2, Clock, ArrowLeft } from "lucide-react";

const ORDER = { needs_you: 0, ready: 1, no_match: 2, refused: 3, waiting: 4, saved: 5, undone: 6 };

const Counts = ({ files }) => {
  const n = (s) => files.filter((f) => f.status === s).length;
  return (
    <p data-testid="bulk-counts" className="text-sm text-slate-700">
      <b>{files.length}</b> file{files.length === 1 ? "" : "s"} · <b className="text-amber-700">{n("needs_you")}</b> need you to choose ·{" "}
      <b className="text-emerald-700">{n("ready")}</b> ready for your Save · <b className="text-red-700">{n("no_match")}</b> no exact VIN match ·{" "}
      <b>{n("refused")}</b> not taken ·{" "}
      <b>{n("saved")}</b> saved · <b>{n("waiting")}</b> waiting
    </p>
  );
};

export default function BulkUploadPage() {
  const [batch, setBatch] = useState(null);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const [refusedTick, setRefusedTick] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/bulk-upload/batches/mine/latest").then(({ data }) => {
      if (data?.id) { setBatch(data); setFiles(data.files || []); }
    }).catch(() => {});
  }, []);

  const send = async (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    let b = batch;
    try {
      if (!b) { const { data } = await api.post("/bulk-upload/batches"); b = data; setBatch(data); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
    // ONE FILE AT A TIME, so no single request is ever too big for the network, and one bad file
    // never stops the rest.
    for (let i = 0; i < picked.length; i += 1) {
      setProgress({ done: i, total: picked.length, name: picked[i].name });
      const fd = new FormData();
      fd.append("file", picked[i]);
      try {
        const { data } = await api.post(`/bulk-upload/batches/${b.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.duplicate_of_this_batch) {
          toast.message(`${picked[i].name}: the same file as ${data.original_name}, already in this batch — not added twice.`);
        }
        setFiles((cur) => [data, ...cur.filter((x) => x.id !== data.id)]);
      } catch (e) {
        toast.error(`${picked[i].name}: ${formatApiErrorDetail(e.response?.data?.detail) || "could not be stored — nothing was saved."}`);
      }
    }
    setProgress(null);
    setRefusedTick((t) => t + 1);
    if (inputRef.current) inputRef.current.value = "";
  };

  // A NEW BATCH IS MADE ON THE SERVER, so a reload opens the new (empty) batch, not the old one.
  const fresh = async () => {
    try { const { data } = await api.post("/bulk-upload/batches"); setBatch(data); setFiles([]); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sorted = [...files].sort((a, b) => (ORDER[a.status] ?? 9) - (ORDER[b.status] ?? 9));

  return (
    <div data-testid="bulk-upload-page" className="max-w-5xl mx-auto space-y-6">
      <div className="space-y-1">
        <Link to="/catch-up" data-testid="bulk-back-catch-up" className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" />Catch-up tools</Link>
        <h1 className="font-display text-2xl font-bold tracking-tight">Bulk Upload — build sheets & dataplates</h1>
        <p className="text-sm text-slate-600 max-w-3xl">
          Drop many files at once. <b>Nothing goes on a truck or part until you press Save on that file.</b>{" "}
          A build sheet only lines up with a truck on an exact, full VIN. A dataplate is suggested by its VIN or engine
          serial and you choose the truck or the engine part. Every file can be undone on its own.
        </p>
      </div>

      <BulkGate />

      <div data-testid="bulk-drop-zone" onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); send(e.dataTransfer.files); }}
        className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/70 px-6 py-10 text-center hover:border-[#B01E28] transition-colors">
        <UploadCloud className="w-8 h-8 mx-auto text-slate-400" />
        <p className="mt-2 text-sm text-slate-700">Drag build-sheet PDFs, Cummins dataplate PDFs (QuickServe or parts.cummins.com) and Freightliner dealer sheets (.xlsx, .xls or .csv) here</p>
        <p data-testid="bulk-freightliner-vin-note" className="mt-1 text-xs text-slate-500 max-w-xl mx-auto">
          The Freightliner sheet prints no VIN inside it, so it lines up only when its file name starts with the truck's full 17-character VIN.
        </p>
        <input ref={inputRef} data-testid="bulk-file-input" type="file" multiple accept=".pdf,application/pdf,.xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          className="hidden" onChange={(e) => send(e.target.files)} />
        <Button data-testid="bulk-choose-files" variant="outline" onClick={() => inputRef.current?.click()} disabled={!!progress}
          className="mt-3 rounded-lg h-10">Choose files</Button>
        {progress && (
          <p data-testid="bulk-upload-progress" className="mt-3 text-xs font-mono text-slate-600 inline-flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Storing {progress.done + 1} of {progress.total} — {progress.name}
          </p>
        )}
      </div>

      <RefusedUploads params={{ where: "bulk" }} refreshKey={refusedTick} testid="bulk-refused" />

      {files.length > 0 && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Counts files={files} />
            <span className="flex-1" />
            <Link to="/dataplates/waiting" data-testid="bulk-link-waiting" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />Waiting Dataplates
            </Link>
            <Button data-testid="bulk-new-batch" size="sm" variant="ghost" onClick={fresh} disabled={!!progress} className="text-xs h-8">Start a new batch</Button>
          </div>
          {sorted.map((f) => (
            <BulkFileRow key={f.id} f={f} onChanged={(row) => setFiles((cur) => cur.map((x) => (x.id === row.id ? row : x)))} />
          ))}
        </div>
      )}
    </div>
  );
}
